import { create, select } from "d3";
import { html, render } from "lit";
import { CFacturaCFDIComplementoConcepto, ICatalogoSATClaveProductServ, ICatalogoSATClaveUnidad, IFacturaClienteAlumno } from "../../../../data/entidad/Factura";
import { _SvProcesaFacturaCatalogo } from "../../../../data/modulo/FacturaCatalogo";
import { _SvFacturaObtenerCatalogoSATClaveUnidad } from "../../../../data/modulo/FacturaCatalogoSAT";
import { _SvFacturaObtenerClaveProdServSATBase } from "../../../../data/modulo/FinanzaCargo";
import _L from "../../../../util/Labels";
import { SelectV2 } from "../../../controlD3/SelectV2";
import { HTMLTooltipComponent } from "../../../controlWC/TooltipComponent";
import { UIUtilElement } from "../../../util/Element";
import { UIUtilFormat } from "../../../util/Format";
import { AlumnoEdoCtaFacturacionUtil as Util } from "./FacturacionUtil";
import { ICFDIInfoItemPreFinal } from "./FacturacionViewCFDIInfo";
import { UIUtilViewAlumnoEdoCtaFacturacionViewCFDIInfoComplemento as cfdiComl } from "./FacturacionViewCFDIInfoComplemento";
import { UIUtilViewAlumnoEdoCtaFacturacionViewCFDIItem as cfdiItem } from "./FacturacionViewCFDIInfoItemForm";
import { IFacturaCFDIConceptoFiscalAdvanced, IFacturaCFDIConceptosFiscalesProcesadosAdvanced } from "./FacturacionViewMovs";
import ic_info from "/image/iconos/ic_infomark.svg?raw";

// ***********************************************************************
// Controlador de lista de CFDIs a crear
// ***********************************************************************

type CFDIItemForm = cfdiItem.CFDIItemForm

type TProdServItemData = ICatalogoSATClaveProductServ & { full_name: string }
type TClaveUnidadItemData = ICatalogoSATClaveUnidad & { full_name: string }

interface CFDISListControl {
    /**
     * Establece los datos del cliente y los movimientos de pagos agrupados.
     * @param cliente Cliente seleccionado.
     * @param gruposCFDI Grupos de CFDI seleccionados.
     * @returns El control actualizado.
     */
    _SetData(cliente: IFacturaClienteAlumno, gruposCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]): CFDISListControl
    /**
     * Obtiene la cantidad de CFDIs a crear.
     * @returns Número de CFDIs.
     */
    _GetCountData(): number
    /**
     * Obtiene los datos finales de los CFDIs.
     * @returns Un arreglo con la información de los CFDIs.
     */
    _GetDataFinal(): ICFDIInfoItemPreFinal[]
    /**
     * Obtiene los controles asociados a los CFDIs.
     * @returns Un objeto con los controles de CFDIs.
     */
    _GetControls(): {
        CFDIGroup: string,
        Form: CFDIItemForm,
        CFDISelectProdServControls: SelectV2<TProdServItemData>[],
        CFDISelectUnidadControls: SelectV2<TClaveUnidadItemData>[],
        CFDIComplementoControls: cfdiComl.FacturaComplementoConceptoComponent[],
    }[]
}
export function CreateCFDISListControl(idAlumno: number, idEscuela: number, container: TSelectionHTML): CFDISListControl {
    const movimientosPagosCFDIMap: Map<string, IFacturaCFDIConceptosFiscalesProcesadosAdvanced> = new Map()
    const cfdisFormsMap = new Map<string, CFDIItemForm>()
    const prodservClavesControlMap = new Map<string, SelectV2<TProdServItemData>>()
    const unidadClavesControlMap = new Map<string, SelectV2<TClaveUnidadItemData>>()
    const complementoAConceptoControlMap = new Map<string, cfdiComl.FacturaComplementoConceptoComponent>()


    const listView = Util._CreateListControl<IFacturaCFDIConceptosFiscalesProcesadosAdvanced>({ addMarginTop: true, addMarginBottom: true })
        ._SetParent(container)
        ._SetCreateTemplate((itemContent, d) => {
            itemContent.append("b").attr("class", "cfdi_head")
                .style("background-color", "var(--color_borderbox1)")
                .style("padding", "0 var(--padding1)")
                .style("border-radius", "var(--border_radius_base)")
            itemContent.append("div").attr("class", "cfdi_info").style("margin-top", "var(--padding2)")
        })
        ._SetUpdateItem((itemContent, cfdiInfo) => {
            // CFDI HEAD
            itemContent.select<HTMLElement>(".cfdi_head")
                .classed("hide", movimientosPagosCFDIMap.size <= 1)
                .text(movimientosPagosCFDIMap.size > 1 ? cfdiInfo._NombreCFDI : "")
                .call(elem => elem.on("click", () => {
                    elem.node().scrollIntoView({ behavior: "smooth", inline: "nearest" })
                }))
            //_L("panelfinanzasedocuenta_factura.cfdi") + " " + d.Id
            // CFDI FORM
            cfdisFormsMap.get(cfdiInfo.GrupoCFDI).__SetContainer(itemContent)

            // CFDI INFO: Conceptos tabla
            render(html`
                <b>${_L("panelfinanzasedocuenta_factura.conceptos")}</b>

                <table style="min-width:100%;margin-top:var(--padding1);border-top:1px solid var(--color_borderbox1);">
                    <tr>
                        <th style="width:30%;">${_L("panelfinanzasedocuenta_factura.descripcion")}</th>
                        <th style="width:30%;">${_L("panelfinanzasedocuenta_factura.claveprodserv")}</th>
                        <th style="width:25%;">${_L("panelfinanzasedocuenta_factura.unidad")}</th>
                        <th style="width:15%;text-align:right;">${_L("panelfinanzasedocuenta_factura.monto")}</th>
                        <th style="width:5%;text-align:right;">${_L("panelfinanzasedocuenta_factura.complemento")}</th>
                    </tr>
                    ${cfdiInfo.ConceptosFiscales.map(concepto =>
                html`<tr>
                        <td class="descripcion" name="${concepto._Identificador}">${concepto.Descripcion}</td>
                        <td class="clave_prodserv" name="${concepto._Identificador}"></td>
                        <td class="clave_unidad" name="${concepto._Identificador}"></td>
                        <td style="text-align:right;">${UIUtilFormat._CurrencyFmt(concepto.Importe)}</td>
                        <td class="complemento" name=${concepto._Identificador}></td>
                    </tr>`)}
                </table>
                `, itemContent.select(".cfdi_info").node() as HTMLElement);

            itemContent.select(".cfdi_info").raise(); // Asegura que esté enseguida del formulario

            // Agregar tooltips a la columna de Descripción
            itemContent.selectAll<HTMLTableCellElement, any>(".descripcion").each((_, i, cells) => { // Descripcion
                const td = cells[i]
                select(td).selectAll(":scope>*").remove()
                const idConcepto: string = td.getAttribute("name")
                const concepto = cfdiInfo.ConceptosFiscales.find(c => c._Identificador == idConcepto)
                const icInfo: HTMLElement = UIUtilElement._CreateElementFromHTML(ic_info)
                icInfo.classList.add("ic_info")
                icInfo.style.width = "var(--padding3)"
                icInfo.style.marginLeft = "var(--padding1)"
                td.append(icInfo)
                const tooltip = document.createElement("wc-tooltip") as HTMLTooltipComponent
                tooltip._Position = "top";
                td.append(tooltip);
                tooltip._SetObservedElementsAdvanced({
                    Target: icInfo as HTMLElement,
                    HTMLBasic: _L("panelfinanzasedocuenta_factura.movs_asociados_a_concepto") + `<table>
                    ${concepto.MovimientoFinanza.map(c => {
                        return `<tr><td>`
                            + `<div><b>${_L("panelfinanzasedocuenta.tag_cargo")}:</b> ${c.NombreCargo}</div>`
                            + `<div><b>${_L("panelfinanzasedocuenta_factura.periodo")}:</b> ${c.NombrePeriodo}</div>`
                            + `<div><b>${_L("panelfinanzasedocuenta_factura.monto")}:</b> ${UIUtilFormat._CurrencyFmt(c.Monto)}</div>`
                            + `</td></tr>`
                    }).join("")}
                    </table>`
                })
            })
            // Controles Select: Clave Prod/Serv SAT, Clave Unidad SAT, Complemento
            itemContent.selectAll<HTMLTableCellElement, any>(".clave_prodserv").each((_, i, cells) => { // Clave Prod/Serv
                const td = cells[i]
                const idConcepto: string = td.getAttribute("name")
                select(td).selectAll(":scope>*").remove()
                td.appendChild(prodservClavesControlMap.get(idConcepto)._controlSelection.node())
            })
            itemContent.selectAll<HTMLTableCellElement, any>(".clave_unidad").each((_, i, cells) => { // Clave Prod/Serv
                const td = cells[i]
                const idConcepto: string = td.getAttribute("name")
                select(td).selectAll(":scope>*").remove()
                td.appendChild(unidadClavesControlMap.get(idConcepto)._controlSelection.node())
            })
            itemContent.selectAll<HTMLTableCellElement, any>(".complemento").each((_, i, cells) => { // Complemento
                const td = cells[i]
                const idConcepto: string = td.getAttribute("name")
                select(td).selectAll(":scope>*").remove()
                td.appendChild(complementoAConceptoControlMap.get(idConcepto))
            })
        })


    const result: CFDISListControl = {
        _GetCountData: () => movimientosPagosCFDIMap.size,
        _SetData: (cliente: IFacturaClienteAlumno, gruposCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]) => {
            movimientosPagosCFDIMap.clear()
            gruposCFDI.forEach(cfdi => {
                const movimientoPagosCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = {
                    ...cfdi,
                    ConceptosFiscales: cfdi.ConceptosFiscales.map((concepto: IFacturaCFDIConceptoFiscalAdvanced) => {
                        let selectProdServCtrl: SelectV2<TProdServItemData>
                        let selectUnidadCtrl: SelectV2<TClaveUnidadItemData>
                        let complCtrl: cfdiComl.FacturaComplementoConceptoComponent

                        if (!prodservClavesControlMap.has(concepto._Identificador)) { // No se han creado controles
                            // Crear Select Producto / Servicio SAT
                            selectProdServCtrl = fnCreatePagoClavePSControl(concepto, idEscuela)
                            selectProdServCtrl._valueSelect(concepto.ClaveProdServSAT)
                            if (!!selectProdServCtrl._dataSelected[0] && !!selectProdServCtrl._dataSelected[0]?.key) {
                                selectProdServCtrl._disabled = true
                                selectProdServCtrl._IconCollapseSelection.classed("hide", true)
                            }
                            prodservClavesControlMap.set(concepto._Identificador, selectProdServCtrl)

                            // Crear Select Unidad SAT
                            selectUnidadCtrl = fnCreatePagoClaveUnidadControl(concepto, idEscuela)
                            selectUnidadCtrl._valueSelect(concepto.ClaveUnidadSAT)
                            if (!!selectUnidadCtrl._dataSelected[0] && !!selectUnidadCtrl._dataSelected[0]?.key) {
                                selectUnidadCtrl._disabled = true
                                selectUnidadCtrl._IconCollapseSelection.classed("hide", true)
                            }
                            unidadClavesControlMap.set(concepto._Identificador, selectUnidadCtrl)

                            complCtrl = new cfdiComl.FacturaComplementoConceptoComponent();
                            complCtrl._idAlumno = idAlumno;
                            complCtrl._dataComplementoIEDU = concepto._Complemento;
                            complCtrl._required = concepto.ComplementoRequerido === CFacturaCFDIComplementoConcepto.IEDU;
                            complementoAConceptoControlMap.set(concepto._Identificador, complCtrl)
                        } else {
                            selectProdServCtrl = prodservClavesControlMap.get(concepto._Identificador)
                            selectUnidadCtrl = unidadClavesControlMap.get(concepto._Identificador)
                            complCtrl = complementoAConceptoControlMap.get(concepto._Identificador)
                        }
                        console.debug("Update Clave SAT, selectedValue = ", selectProdServCtrl._dataSelected[0]?.key)
                        return <IFacturaCFDIConceptoFiscalAdvanced>{
                            ...concepto,
                            ClaveProdServSAT: (selectProdServCtrl._dataSelected[0]?.key || "") as string,
                            ClaveUnidadSAT: (selectUnidadCtrl._dataSelected[0]?.key || undefined) as string | number,
                            NombreUnidadSAT: (selectUnidadCtrl._dataSelected[0]?.name || undefined) as string,
                            _Complemento: complCtrl._dataComplementoIEDU,
                        }
                    })
                }

                movimientosPagosCFDIMap.set(cfdi.GrupoCFDI, movimientoPagosCFDI)
            })

            const cfdisFormsMapAux = new Map(cfdisFormsMap)
            cfdisFormsMap.clear()
            movimientosPagosCFDIMap.forEach(d => {
                if (cfdisFormsMapAux.has(d.GrupoCFDI)) {
                    cfdisFormsMap.set(d.GrupoCFDI, cfdisFormsMapAux.get(d.GrupoCFDI))
                } else {
                    cfdisFormsMap.set(d.GrupoCFDI, cfdiItem.CrearCFDIItemForm(idEscuela))
                }
                cfdisFormsMap.get(d.GrupoCFDI)
                    .__SetClient(cliente)
                    .__SetMovimientoPagosAgrupados(d)
            })

            listView._SetItems([...movimientosPagosCFDIMap.values()])
            return result
        },
        _GetDataFinal: () => [...movimientosPagosCFDIMap.entries()].map(([GrupoCFDI, { _NombreCFDI, ConceptosFiscales }]) => {
            const cfdiInfo = cfdisFormsMap.get(GrupoCFDI)._Data
            const itemResult: ICFDIInfoItemPreFinal = {
                NombreCFDI: _NombreCFDI,
                fechaTimbrado: cfdiInfo.fechaTimbrado,
                Fecha: null,
                UsoCFDI: cfdiInfo.UsoCFDI,
                FormaPago: cfdiInfo.FormaPago,
                NumOrder: cfdiInfo.NumOrder,
                CondicionPago: cfdiInfo.CondicionPago,
                Banco: cfdiInfo.Banco,
                Cuenta: cfdiInfo.Cuenta,
                Comentarios: cfdiInfo.Comentarios,
                EnviarCorreo: cfdiInfo.EnviarCorreo,
                Conceptos: ConceptosFiscales.map<ICFDIInfoItemPreFinal["Conceptos"][number]>(({ MovimientoFinanza, _Identificador }) => ({
                    IdsMovPagos: MovimientoFinanza.map(movPago => movPago.Id),
                    ClaveProdServSAT: prodservClavesControlMap.get(_Identificador)._dataValueMemberSelected[0],
                    ClaveUnidadSAT: unidadClavesControlMap.get(_Identificador)._dataValueMemberSelected[0],
                    NombreUnidadSAT: unidadClavesControlMap.get(_Identificador)._dataSelected[0].name,
                    Complemento: complementoAConceptoControlMap.get(_Identificador)._dataComplementoIEDU,
                })),
            }
            return itemResult
        }),
        _GetControls: () => [...movimientosPagosCFDIMap.entries()].map(([GrupoCFDI, cfdiInfo]) => ({
            CFDIGroup: GrupoCFDI,
            Form: cfdisFormsMap.get(GrupoCFDI),
            CFDISelectProdServControls: cfdiInfo.ConceptosFiscales.map(({ _Identificador }) => prodservClavesControlMap.get(_Identificador)),
            CFDISelectUnidadControls: cfdiInfo.ConceptosFiscales.map(({ _Identificador }) => unidadClavesControlMap.get(_Identificador)),
            CFDIComplementoControls: cfdiInfo.ConceptosFiscales.map(({ _Identificador }) => complementoAConceptoControlMap.get(_Identificador)),
        })),
    }
    return result
}

const fnCreatePagoClavePSControl = (concepto: IFacturaCFDIConceptoFiscalAdvanced, idEscuela: number) => {
    const control = new SelectV2<TProdServItemData, "key", "monoselect">({
        Parent: create("div"), // AUX PARENT
        Type: "monoselect",
        ValueMember: "key",
        DisplayMember: "full_name",
        ListWidth: "450px",
        ShowAndEnableSearchText: true,
        RecyclerView: true,
        OnChange: (_, dato) => {
            concepto.ClaveProdServSAT = dato.key
            concepto.NombreProdServSAT = dato.name
        },
        Data: (() => {
            const itemDefault: ICatalogoSATClaveProductServ = !concepto.ClaveProdServSAT
                ? null
                : <ICatalogoSATClaveProductServ>{ key: concepto.ClaveProdServSAT, name: concepto.NombreProdServSAT, complement: "", full_name: `${concepto.ClaveProdServSAT} - ${concepto.NombreProdServSAT || ""}` }
            const resolveData = (res: ICatalogoSATClaveProductServ[]) => res.map(d => ({ ...d, full_name: (d.key + " - " + d.name) }))
            const res = _SvProcesaFacturaCatalogo(_SvFacturaObtenerClaveProdServSATBase(idEscuela, itemDefault)).then(resolveData)
            return res
        })()
    })
    control._controlSelection.style("border", "none")
    return control
}
const fnCreatePagoClaveUnidadControl = (concepto: IFacturaCFDIConceptoFiscalAdvanced, idEscuela: number) => {
    const control = new SelectV2<TClaveUnidadItemData, "key", "monoselect">({
        Parent: create("div"), // AUX PARENT
        Type: "monoselect",
        ValueMember: "key",
        DisplayMember: "full_name",
        ListWidth: "350px",
        ShowAndEnableSearchText: true,
        // RecyclerView: true,
        OnChange: (_, dato) => {
            concepto.ClaveUnidadSAT = dato.key
            concepto.NombreUnidadSAT = dato.name
        },
        Data: (() => {
            // const itemDefault: TClaveUnidadItemData = null // !p.ClaveProdServSAT ? null : { key: p.ClaveProdServSAT, name: p.NombreProdServSAT, complement: "" }
            const resolveData = (res: ICatalogoSATClaveUnidad[]) => res.map(d => ({ ...d, full_name: (d.key + " - " + d.name) }))
            // const res = _ProcesaFacturaCatalogo(_ObtenerClaveProdServSATBase(idEscuela, itemDefault)).then(resolveData)
            const res = _SvProcesaFacturaCatalogo(_SvFacturaObtenerCatalogoSATClaveUnidad(idEscuela)).then(resolveData)
            return res
        })()
    })
    control._controlSelection.style("border", "none")
    return control
}